import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations';
import api from '../utils/api';

const Destinations = () => {
  const { language, isRTL } = useLanguage();
  const t = translations[language];
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDestinations = async () => {
      try {
        const res = await api.get('/destinations');
        setDestinations(res.data);
      } catch (error) {
        console.error('Error fetching destinations:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDestinations();
  }, []);

  return (
    <div className="min-h-screen bg-blackC">
      {/* Hero Section */}
      <section className="relative pt-32 lg:pt-40">
        <div className="absolute inset-0 bg-gradient-to-b from-dark to-blackC" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <p className="section-subtitle animate-fade-in-up opacity-0 animation-delay-100">{t.destinationsSubtitle}</p>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-cream mb-6 animate-fade-in-up opacity-0 animation-delay-200">
              {t.popularDestinations}
            </h1>
            <div className="divider-gold mx-auto animate-fade-in-up opacity-0 animation-delay-300" />
            <p className="max-w-2xl mx-auto text-gray-silver text-lg mt-8 animate-fade-in-up opacity-0 animation-delay-400">
              {language === 'en'
                ? 'Explore our most popular routes and destinations across Egypt with our premium limousine service.'
                : 'استكشف أشهر الوجهات والمسارات في مصر مع خدمة الليموزين الفاخرة لدينا.'}
            </p>
          </div>
        </div>
      </section>

      {/* Destinations Grid */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="spinner" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {destinations.map((dest, index) => (
                <div
                  key={dest._id}
                  className="group relative overflow-hidden bg-dark border border-gray-dark hover:border-primary/50 transition-all duration-500"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  {/* Decorative corner */}
                  <div className="absolute top-0 right-0 w-20 h-20 border-t border-r border-primary/20 group-hover:border-primary/40 transition-colors duration-500 z-10" />
                  <div className="absolute bottom-0 left-0 w-20 h-20 border-b border-l border-primary/20 group-hover:border-primary/40 transition-colors duration-500 z-10" />

                  {/* Image if available */}
                  {dest.image?.url && (
                    <div className="absolute inset-0 overflow-hidden">
                      <img
                        src={dest.image.url}
                        alt={language === 'en' ? dest.name : dest.nameAr}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blackC/40 to-blackC" />
                    </div>
                  )}

                  <div className="p-8 relative z-10">
                    {/* Location Icon - only show if no image */}
                    {!dest.image?.url && (
                      <div className="w-14 h-14 rounded-full bg-blackC border border-primary/30 flex items-center justify-center mb-6 group-hover:bg-primary group-hover:border-primary transition-all duration-500">
                        <svg className="w-6 h-6 text-primary group-hover:text-blackC transition-colors duration-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                    )}

                    {/* Title */}
                    <h3 className="font-display text-3xl text-cream mb-4">
                      {language === 'en' ? dest.name : dest.nameAr}
                    </h3>

                    {/* Distance */}
                    {dest.distance && (
                      <div className="flex items-center gap-2 text-gray-light mb-6">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                        </svg>
                        <span className="text-sm">{dest.distance} km</span>
                      </div>
                    )}

                    {/* Price */}
                    <div className="mb-8">
                      {dest.pricingType === 'fixed' && dest.fixedPrice && (
                        <div>
                          <p className="text-gray-light text-xs uppercase tracking-wide mb-1">{t.startingFrom}</p>
                          <p className="text-primary font-display text-3xl">{dest.fixedPrice} <span className="text-lg">EGP</span></p>
                        </div>
                      )}
                      {dest.pricingType === 'perKm' && dest.pricePerKm && (
                        <div>
                          <p className="text-gray-light text-xs uppercase tracking-wide mb-1">{t.price}</p>
                          <p className="text-primary font-display text-3xl">{dest.pricePerKm} <span className="text-lg">EGP/km</span></p>
                        </div>
                      )}
                    </div>

                    {/* Book Button */}
                    <Link
                      to="/book"
                      className="w-full btn-secondary flex items-center justify-center gap-2"
                    >
                      {t.book}
                      <svg className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {!loading && destinations.length === 0 && (
            <div className="text-center py-20">
              <p className="text-gray-light text-lg">
                {language === 'en' ? 'No destinations available.' : 'لا توجد وجهات متاحة.'}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Custom Route CTA */}
      <section className="py-24 bg-dark border-t border-primary/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 rounded-full bg-blackC border border-primary/30 flex items-center justify-center mx-auto mb-8">
            <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
          </div>
          <h2 className="font-display text-3xl md:text-4xl text-cream mb-6">
            {language === 'en' ? 'Custom Destination?' : 'وجهة مخصصة؟'}
          </h2>
          <p className="text-gray-silver mb-8 max-w-xl mx-auto">
            {language === 'en'
              ? "Don't see your destination? We offer custom routes to anywhere in Egypt. Contact us for a personalized quote."
              : 'لا ترى وجهتك؟ نقدم مسارات مخصصة لأي مكان في مصر. تواصل معنا للحصول على عرض سعر مخصص.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/book?type=anywhere" className="btn-primary">
              {t.anywhere}
            </Link>
            <Link to="/contact" className="btn-secondary">
              {t.contactUs}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Destinations;